import { parse } from "query-string";
import React from "react";

import AlbumHeader from "@/mk2/apps/blogs/components/AlbumHeader";
import AlbumImagePreview from "@/mk2/apps/blogs/components/AlbumImagePreview";
import ShareButton from "@/mk2/apps/blogs/components/ShareButton";
import { AUTH_KEY_NAME, CommentsDesign } from "@/mk2/apps/blogs/constants";
import { Props } from "@/mk2/apps/blogs/containers/BlogsAlbumDetail/BlogsAlbumDetailPage";
import Comments from "@/mk2/apps/blogs/containers/Comments/Comments";
import { blogsAlbumPhotoUrl, blogsPostShareUrl } from "@/mk2/apps/blogs/urls";
import { forumUntrustworthyUserFeedUrl } from "@/mk2/apps/forum/urls";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { DeletedPostAlert } from "@/mk2/components/DeletedPostAlert";
import { Emojify } from "@/mk2/components/Emojify";
import { ExpandableClamp } from "@/mk2/components/ExpandableClamp";
import { Link } from "@/mk2/components/Link";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import {
    BLOGS_ALBUM_DESCRIPTION_SHOW_MORE,
    LIKE_BUTTON_LABEL,
    POST_REMOVE_COMPLAINTS,
} from "@/mk/autogenerated/translation";
import { canManageBlogs } from "@/mk/bazaar/common/userUtils";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./BlogsAlbumDetail.mscss";

export default class BlogsAlbumDetail extends React.Component<Props> {
    private astToHtmlCache = cacheLast(true);

    public componentDidUpdate(prevProps) {
        const {
            location: locationA,
            match: {
                params: { username: usernameA, postSlug: postSlugA },
            },
        } = this.props;
        const {
            location: locationB,
            match: {
                params: { username: usernameB, postSlug: postSlugB },
            },
        } = prevProps;
        const accessKeyA = parse(locationA.search)[AUTH_KEY_NAME];
        const accessKeyB = parse(locationB.search)[AUTH_KEY_NAME];
        if (usernameA === usernameB && postSlugA === postSlugB && accessKeyA !== accessKeyB) {
            this.load();
        }
    }

    public render() {
        const { loadingState, post } = this.props;

        return (
            <div className={styles.BlogsAlbumDetail}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    onFailureToast={this.renderFailureToast}
                    hasData={!!post}
                />
            </div>
        );
    }

    private renderSuccess = () => {
        const { post, location, history, requestUser, requestUserPermissions, isMobile } =
            this.props;

        const isBlogsAdmin = canManageBlogs(requestUserPermissions);

        // prepare album description body
        let bodyChunks: React.ReactChild[] = null;

        if (post.contentAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: post.contentASTAdditionalData
                    ? post.contentASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderLinksOfUntrustworthy: post.author.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                    : false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_LINK,
            };
            bodyChunks = this.astToHtmlCache(
                () => astToHtml(post.contentAST, additionalData),
                post.contentAST,
            );
        }

        return (
            <div className={styles.BlogsAlbumDetail}>
                <AlbumHeader post={post} />
                {post.deletedAt && (
                    <div className={styles.BlogsAlbumDetail__announcements}>
                        {post.deletedAt && (
                            <DeletedPostAlert
                                deletedByRole={post.deletedByRole}
                                deletedBy={post.deletedBy?.username}
                                deletedAt={post.deletedAt}
                                reason={post.deletedReason}
                            />
                        )}
                    </div>
                )}
                <div className={styles.BlogsAlbumDetail__description}>
                    <ExpandableClamp expandLabel={BLOGS_ALBUM_DESCRIPTION_SHOW_MORE} clamp={13}>
                        <div className={styles.BlogsAlbumDetail__description__text}>
                            <Emojify size={17}>{bodyChunks}</Emojify>
                        </div>
                    </ExpandableClamp>
                </div>
                <Complaint
                    id={post.id}
                    entity={ComplaintTargetType.PhotoblogPost}
                    className={styles.BlogsAlbumDetail__complaint}
                    scrollOffset={44 + 44 + 30 /* header + 30px spacing */}
                    inForum={false}
                    inGroups={false}
                />
                {isBlogsAdmin && (
                    <Complaints
                        className={styles.BlogsAlbumDetail__complaints}
                        id={post.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                        adminTheme={isBlogsAdmin}
                    />
                )}

                <div className={styles.BlogsAlbumDetail__images}>
                    {post.images.map((image, idx) => {
                        return (
                            <Link
                                to={url(blogsAlbumPhotoUrl, {
                                    username: post.author.username,
                                    postSlug: post.slug,
                                    imageId: image.id,
                                })}
                                key={`photo-${idx}`}
                            >
                                <AlbumImagePreview
                                    photoUrl={photoUrl(image.photo, "snx224")}
                                    photoWidth={image.photo.width}
                                    photoHeight={image.photo.height}
                                    complaintsCount={image.complaintsCount}
                                    commentsCount={image.commentsCount}
                                    likesCount={image.likesCount}
                                    isLiked={image.likeable.isLiked}
                                    createdTime={image.createdTime}
                                    previewWidth={isMobile ? 118 : 140}
                                    previewHeight={isMobile ? 118 : 140}
                                    className={styles.BlogsAlbumDetail__image}
                                />
                            </Link>
                        );
                    })}
                </div>

                <div className={styles.BlogsAlbumDetail__footer}>
                    <div className={styles.BlogsAlbumDetail__footer__likesAndShares}>
                        <Like
                            entity="photoblog.post"
                            id={post.id}
                            type={LikeType.CountIconLabel}
                            className={
                                styles.BlogsAlbumDetail__footer__likesAndShares__likeAndShare
                            }
                            label={LIKE_BUTTON_LABEL}
                        />
                    </div>
                    <div className={styles.BlogsAlbumDetail__footer__likesAndShares}>
                        <ShareButton
                            sharesCount={post.sharesCount || 0}
                            className={
                                styles.BlogsAlbumDetail__footer__likesAndShares__likeAndShare
                            }
                            link={url(blogsPostShareUrl, { postId: post.id })}
                        />
                    </div>
                </div>

                <Comments
                    parentPost={post}
                    count={post.commentsCount}
                    design={CommentsDesign.Normal}
                    isPreloaded
                    location={location}
                    history={history}
                    hasBlueToolbar
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isMobile={isMobile}
                />
            </div>
        );
    };

    private renderFailureToast = () => {
        /* nothing to do */
    };

    private load = () => {
        const {
            match: {
                params: { username, postSlug },
            },
            onLoad,
            location,
        } = this.props;
        const q = parse(location.search);

        onLoad(username, postSlug, q[AUTH_KEY_NAME] as string);
    };
}
